import { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import findPlano from '../../service/findPlano';
import putPlano from '../../service/putPlano';
import inativarPlano from '../../service/inativarPlano';
import ativarPlano from '../../service/ativarPlano';
import getOpcoesConsultaAgrupada from '../../service/getOpcoesConsultaAgrupada';
import { Button } from 'primereact/button';
import { SortOrder } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Nullable } from 'primereact/ts-helpers';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';




export default function PlanoList({onEdit}:{onEdit:(plano:any)=>void}) {
  const { t } = useTranslation();
  const [planos, setPlanos] = useState(undefined);
  const [totalRecords, setTotalRecords] = useState(0);
  const [descricao, setDescricao] = useState<Nullable<String | null>>(null);
  const toast = useRef<Toast>(null);
  const [opcoesAgrupada, setOpcoesAgrupada] = useState<any[]>([]);
  const [showReajuste, setShowReajuste] = useState<boolean>(false);
  const [reajustePlano, setReajustePlano] = useState<any>(null);
  const [reajusteData, setReajusteData] = useState<Date|null|undefined>(null);
  const [reajustePercentual, setReajustePercentual] = useState<number|null|undefined>(null);
  const [lazyState, setlazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    descricao: descricao,
    ativo: true as boolean | null,
    sortField: 'descricao',
    sortOrder: SortOrder.ASC
  });

  const STATUS_TODOS = 'TODOS';
  const statusOptions = [
    { label: t('todos'), value: STATUS_TODOS },
    { label: t('ativo'), value: 'true' },
    { label: t('inativo'), value: 'false' },
  ];
  const statusDropdownValue = lazyState.ativo === null ? STATUS_TODOS : String(lazyState.ativo);

  const onPage = (event: any) => {
    setlazyState({
      first: event.first,
      rows: event.rows,
      page: (event.first / event.rows) + 1,
      descricao: descricao,
      ativo: lazyState.ativo,
      sortField: event.sortField,
      sortOrder: event.sortOrder
    });
  };
  const onSort = (event:any) => {
    setlazyState({
      first: 0,
      rows: 10,
      page: 1,
      descricao: descricao,
      ativo: lazyState.ativo,
      sortField: event.sortField,
      sortOrder: event.sortOrder
    });
  };

  const changeDescricao = (desc:Nullable<String>) => {
    if(desc){
      setDescricao(desc);
      setlazyState({
        first: 0,
        rows: 10,
        page: 1,
        descricao: desc,
        ativo: lazyState.ativo,
        sortField: 'descricao',
        sortOrder: SortOrder.ASC
      });
    }
  }

  const changeStatus = (e: any) => {
    const novoAtivo = e.value === STATUS_TODOS ? null : e.value === 'true';
    setlazyState(prev => ({
      ...prev,
      first: 0,
      page: 1,
      ativo: novoAtivo
    }));
  };

  const startContent = (
    <div className="filtros">
      <div className="filtros-grid">
        <div className="filtros-item">
          <label htmlFor="descricao" className="font-bold">{t('descricao')}</label>
          <InputText id="descricao" onBlur={(e)=>changeDescricao(e.target.value)}></InputText>
        </div>
        <div className="filtros-item">
          <label htmlFor="status" className="font-bold">Status</label>
          <Dropdown inputId="status" value={statusDropdownValue} options={statusOptions} optionValue="value" onChange={changeStatus} />
        </div>
      </div>
    </div>
  );

  const loadLazyData = () => {
    findPlano(lazyState).then(data => {setTotalRecords(data.total);setPlanos(data.planos)}).catch(() => {setTotalRecords(0);setPlanos(undefined)});
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (value: Date) => {
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));
  };

  // Rótulo do serviço (para AGRUPADOR usa o identificador amigável, igual ao cadastro)
  const getLabelIdentificador = (identificador:string) => {
    const found = opcoesAgrupada?.filter((s1:any)=> s1.value === identificador);
    return found && found.length > 0 ? found[0].label.split(":")[0] : (identificador || '');
  };

  const getServicoLabel = (ps:any) => {
    return ps.servico.tipoBilhete === 'AGRUPADOR' ? getLabelIdentificador(ps.identificador) : ps.servico.descricao;
  };

  // Valor vigente (planosServicoValor com a maior data)
  const getLatestPsv = (ps:any) => {
    return ps.planosServicoValor.reduce((a:any, b:any) => new Date(b.data).getTime() > new Date(a.data).getTime() ? b : a);
  };

  // Maior data dentro de planosServicoValor de todos os serviços do plano
  const getMaxData = (plano:any): Date | null => {
    const datas: number[] = [];
    plano?.planosServico?.forEach((ps:any) => ps.planosServicoValor?.forEach((psv:any) => datas.push(new Date(psv.data).getTime())));
    return datas.length ? new Date(Math.max(...datas)) : null;
  };

  const percentualPreenchido = reajustePercentual !== null && reajustePercentual !== undefined;

  // Resumo apresentado no modal antes de salvar
  const getResumo = () => {
    if(!reajustePlano || !percentualPreenchido) return [];
    return reajustePlano.planosServico.map((ps:any) => {
      const latest = getLatestPsv(ps);
      const valorAntigo = parseFloat(latest.valor);
      const novoValor = Number((valorAntigo * (1 + (reajustePercentual as number) / 100)).toFixed(2));
      return { servicoLabel: getServicoLabel(ps), valorAntigo, novoValor };
    });
  };

  const openReajuste = (plano:any) => {
    setReajustePlano(plano);
    setReajusteData(null);
    setReajustePercentual(null);
    setShowReajuste(true);
  };

  const closeReajuste = () => {
    setShowReajuste(false);
    setReajustePlano(null);
    setReajusteData(null);
    setReajustePercentual(null);
  };

  const salvarReajuste = () => {
    if(!reajustePlano || !reajusteData || !percentualPreenchido) return;
    const maxData = getMaxData(reajustePlano);
    if(maxData && reajusteData.getTime() < maxData.getTime()){
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'A data deve ser igual ou posterior a ' + formatDate(maxData), life: 5000 });
      return;
    }
    const planosServico = reajustePlano.planosServico.map((ps:any) => {
      const latest = getLatestPsv(ps);
      const novoValor = Number((parseFloat(latest.valor) * (1 + (reajustePercentual as number) / 100)).toFixed(2));
      return { ...ps, planosServicoValor: [...ps.planosServicoValor, { valor: novoValor, data: reajusteData }] };
    });
    const payload = { id: reajustePlano.id, descricao: reajustePlano.descricao, vcm: reajustePlano.vcm ?? null, planosServico };
    putPlano(payload).then((res:any) => {
      if(res && res.toString().includes('Erro')){
        toast.current?.show({ severity: 'error', summary: 'Erro', detail: res, life: 5000 });
      }
      else{
        toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Reajuste aplicado com sucesso!', life: 3000 });
        closeReajuste();
        loadLazyData();
      }
    }).catch((e:any) => {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: e.message || 'Erro ao aplicar reajuste', life: 5000 });
    });
  };

  const actionBodyTemplate = (plano:any) => {
    return <div style={{ display: 'flex', gap: '0.5rem' }}><Button icon="pi pi-pencil" onClick={() => onEdit(plano)} style={{background:'transparent', color:'gray', border: 'none'}} /><Button icon="pi pi-percentage" tooltip="Reajuste" onClick={() => openReajuste(plano)} style={{background:'transparent', color:'gray', border: 'none'}} /></div>;
  };

  const statusBodyTemplate = (plano: any) => {
    return plano.ativo === false
      ? <Tag icon="pi pi-pause-circle" severity="warning" value={t('inativo')} />
      : <Tag icon="pi pi-check-circle" severity="success" value={t('ativo')} />;
  };

  const executarAtivar = (plano: any) => {
    ativarPlano(plano.id).then((mensagem) => {
      toast.current?.show({ severity: 'success', summary: t('sucesso') as string, detail: mensagem, life: 3000 });
      loadLazyData();
    }).catch((error: any) => {
      toast.current?.show({ severity: 'error', summary: t('erro') as string, detail: error?.message || String(error), life: 5000 });
    });
  };

  const executarInativar = (plano: any) => {
    inativarPlano(plano.id).then((mensagem) => {
      if (mensagem && mensagem.includes('Erro')) {
        toast.current?.show({ severity: 'warn', summary: t('erro') as string, detail: mensagem, life: 6000 });
        return;
      }
      toast.current?.show({ severity: 'success', summary: t('sucesso') as string, detail: mensagem, life: 3000 });
      loadLazyData();
    }).catch((error: any) => {
      toast.current?.show({ severity: 'error', summary: t('erro') as string, detail: error?.message || String(error), life: 5000 });
    });
  };

  const confirmarInativar = (plano: any) => {
    confirmDialog({
      message: `${t('confirmarInativarPlano')} "${plano.descricao}"?`,
      header: t('inativar') as string,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('inativar') as string,
      rejectLabel: t('cancelar') as string,
      accept: () => executarInativar(plano)
    });
  };

  const statusActionBodyTemplate = (plano: any) => {
    return plano.ativo === false
      ? <Button icon="pi pi-check-circle" tooltip={t('ativar') as string} tooltipOptions={{ position: 'top' }} onClick={() => executarAtivar(plano)} style={{ background: 'transparent', color: 'gray', border: 'none' }} />
      : <Button icon="pi pi-ban" tooltip={t('inativar') as string} tooltipOptions={{ position: 'top' }} onClick={() => confirmarInativar(plano)} style={{ background: 'transparent', color: '#e24c4c', border: 'none' }} />;
  };



  useEffect(() => {
    loadLazyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[lazyState])

  useEffect(() => {
    getOpcoesConsultaAgrupada().then((data) => setOpcoesAgrupada(data));
  },[])

  const minReajusteData = reajustePlano ? getMaxData(reajustePlano) : null;

  const reajusteFooter = (
    <div style={{display:"flex", justifyContent:"flex-end", paddingTop:"1rem"}} className="flex">
      <Button label="Fechar" severity="secondary" outlined onClick={closeReajuste} />
      <Button label="Salvar" style={{marginRight:"1rem", backgroundColor:'#183462'}} disabled={!reajusteData || !percentualPreenchido} onClick={salvarReajuste} />
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div>
        <Toolbar start={startContent} />
        <br></br>
        <br></br>
        <DataTable value={planos} first={lazyState.first} dataKey="id" paginator={true}  lazy={true}
                        emptyMessage="Sem registros"
                        onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
                        rows={lazyState.rows} totalRecords={totalRecords} onPage={onPage}
                        tableStyle={{ minWidth: '75rem' }}>

           <Column field="descricao" sortable header={t('descricao')}  />
           <Column field="vcm" sortable header={t('vcm')} body={(plano:any) => plano.vcm !== null && plano.vcm !== undefined && plano.vcm !== '' ? formatCurrency(Number(plano.vcm)) : '-'} />
           <Column body={statusBodyTemplate} header="Status" />
           <Column body={actionBodyTemplate} style={{ width: '4rem' }} />
           <Column body={statusActionBodyTemplate} style={{ width: '4rem' }} />
        </DataTable>
      </div>

      <Dialog visible={showReajuste} onHide={closeReajuste} modal style={{ width: '55rem' }}
              header={'Reajuste - ' + (reajustePlano?.descricao ?? '')} footer={reajusteFooter}>
        <div className="grid">
          <div className="col p-fluid flex flex-wrap">
            <label htmlFor="reajusteData" className="font-bold block mb-2 required">{t('data')} </label>
            <Calendar id="reajusteData" value={reajusteData} onChange={(e) => setReajusteData(e.value)} minDate={minReajusteData ?? undefined} showIcon dateFormat="dd/mm/yy" locale="pt" />
          </div>
          <div className="col p-fluid flex flex-wrap">
            <label htmlFor="reajustePercentual" className="font-bold block mb-2 required">{t('percentual')} </label>
            <InputNumber inputId="reajustePercentual" value={reajustePercentual} onValueChange={(e) => setReajustePercentual(e.value)} mode="decimal" minFractionDigits={2} maxFractionDigits={4} suffix=" %" />
          </div>
        </div>
        {reajusteData && percentualPreenchido && (
          <>
            <br></br>
            <DataTable size="small" value={getResumo()} tableStyle={{ minWidth: '40rem' }} emptyMessage="Sem serviços">
              <Column header={t('servico')} body={(row:any) => row.servicoLabel}></Column>
              <Column header="Valor Antigo" body={(row:any) => formatCurrency(row.valorAntigo)}></Column>
              <Column header="Novo Valor" body={(row:any) => formatCurrency(row.novoValor)}></Column>
              <Column header={t('data')} body={() => reajusteData ? formatDate(reajusteData) : ''}></Column>
            </DataTable>
          </>
        )}
      </Dialog>
    </div>
  )
}


